import jwtDecode from 'jwt-decode';
import React, { useContext, useEffect, Suspense } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Actions } from '../enums/ActionEnums';
import { AppConsts } from '../enums/AppConsts';
import { PublicRoutes } from '../enums/RouteEnums';
import { Box, CircularProgress } from '@mui/material';

interface Props {
  Component: React.FunctionComponent | React.LazyExoticComponent<React.FunctionComponent>;
  requiredRole?: string | null;
  isPublic: boolean;
}

interface decoded {
  exp: number;
  name: string;
  family_name: string;
  given_name: string;
  email: string;
  roles: string[];
}

const AuthRoute = ({ Component, isPublic, requiredRole }: Props) => {
  const { state, dispatch } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();

  const redirectToSignIn = () => (
    <Navigate
      to={{
        pathname: PublicRoutes.Login
      }}
      replace
      state={{ path: location.pathname }}
    />
  );

  const navigateToLogin = () => {
    if (!isPublic) {
      navigate(PublicRoutes.Login, { state: { path: location.pathname } });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(AppConsts.AccessToken);
    if (!token) {
      dispatch({ type: Actions.RemoveUser });
      navigateToLogin();
    } else {
      const decoded: decoded = jwtDecode(token);
      const now = new Date().getTime() / 1000;
      if (decoded.exp < now) {
        localStorage.removeItem(AppConsts.AccessToken);
        dispatch({ type: Actions.RemoveUser });
        navigateToLogin();
      }
    }
  }, [location]);

  const isAuthed = isPublic ? true : state.user.token !== null;
  const userRoles: (string | null)[] = state.user.data ? state.user.data.roles : [];
  const accessGranted = requiredRole ? userRoles.includes(requiredRole) : true;

  const renderComponent = () => {
    // Check if Component is a lazy component (LazyExoticComponent has _payload property)
    const isLazyComponent = Component && typeof Component === 'object' && '_payload' in Component;

    const componentElement = <Component />;

    // Wrap lazy components in Suspense, regular components can also be wrapped safely
    if (isLazyComponent) {
      return (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100%'
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          {componentElement}
        </Suspense>
      );
    }
    return componentElement;
  };

  return isAuthed ? (accessGranted ? renderComponent() : redirectToSignIn()) : redirectToSignIn();
};

export default AuthRoute;
